/* eslint-disable i18next/no-literal-string */
import type { ComponentPropsWithoutRef, FC } from "react";
import { useSyncExternalStore } from "react";
import {
    LuActivity,
    LuBox,
    LuClock,
    LuGauge,
    LuHash,
    LuImage,
    LuLayers,
    LuLoader,
    LuTriangle,
    LuZap,
} from "react-icons/lu";
import DiagnosticsStore from "@/features/diagnostics/store/diagnostics-store.ts";
import type { PerformanceMetrics } from "@/features/diagnostics/types";
import type { Nullable } from "@/types";
import { formatNumber } from "@/utils/formatters.ts";
import {
    type CardVariants,
    DiagnosticsCard,
    DiagnosticsCardDescription,
    DiagnosticsCardHeader,
    DiagnosticsCardIcon,
    DiagnosticsCardLabel,
    DiagnosticsCardNull,
    DiagnosticsCardValue,
    DiagnosticsEmpty,
    DiagnosticsGrid,
} from "../lib/diagnostics-card.tsx";

type CardVariant = CardVariants["variant"];

type MetricConfig = {
    icon: FC<ComponentPropsWithoutRef<"svg">>;
    label: string;
    description: string;
    format: (metrics: PerformanceMetrics) => Nullable<string>;
    variant?: CardVariant | ((metrics: PerformanceMetrics) => CardVariant);
};

const METRICS: MetricConfig[] = [
    {
        icon: LuGauge,
        label: "FPS",
        description: "Frames per second based on the actual interval between render ticks.",
        format: (m) => (m.fps > 0 ? `${m.fps}` : null),
        variant: (m) => {
            if (m.fps === 0) return "info";
            if (m.fps >= 59) return "success";
            if (m.fps >= 30) return "warning";

            return "error";
        },
    },
    {
        icon: LuClock,
        label: "Frame time",
        description: "Time spent in the GPU render call for the last frame.",
        format: (m) => (m.current ? `${m.current.frameTime.toFixed(2)} ms` : null),
        variant: (m) => {
            if (!m.current) return "info";
            if (m.current.frameTime < 8) return "success";
            if (m.current.frameTime < 16) return "warning";

            return "error";
        },
    },
    {
        icon: LuActivity,
        label: "Avg frame time",
        description: "Running average of the GPU render call duration across all frames.",
        format: (m) => (m.totalFrames > 0 ? `${m.avgFrameTime.toFixed(2)} ms` : null),
        variant: (m) => {
            if (m.totalFrames === 0) return "info";
            if (m.avgFrameTime < 8) return "success";
            if (m.avgFrameTime < 16) return "warning";

            return "error";
        },
    },
    {
        icon: LuZap,
        label: "Max frame time",
        description: "Slowest GPU render call recorded during this session.",
        format: (m) => (m.totalFrames > 0 ? `${m.maxFrameTime.toFixed(2)} ms` : null),
        variant: (m) => {
            if (m.totalFrames === 0) return "info";
            if (m.maxFrameTime < 16) return "success";
            if (m.maxFrameTime < 33) return "warning";

            return "error";
        },
    },
    {
        icon: LuHash,
        label: "Total frames",
        description: "Total number of frames rendered during this session.",
        format: (m) => (m.totalFrames > 0 ? `${m.totalFrames}` : null),
    },
    {
        icon: LuLayers,
        label: "Draw calls",
        description: "Number of WebGL draw calls issued in the last frame. Fewer is better.",
        format: (m) => (m.current ? formatNumber(m.current.drawCalls) : null),
        variant: (m) => {
            if (!m.current) return "info";
            if (m.current.drawCalls < 50) return "success";
            if (m.current.drawCalls < 200) return "warning";

            return "error";
        },
    },
    {
        icon: LuTriangle,
        label: "Triangles",
        description: "Total triangles rendered in the last frame.",
        format: (m) => (m.current ? formatNumber(m.current.triangles) : null),
        variant: (m) => {
            if (!m.current) return "info";
            if (m.current.triangles < 100_000) return "success";
            if (m.current.triangles < 500_000) return "warning";

            return "error";
        },
    },
    {
        icon: LuBox,
        label: "Geometries",
        description: "Number of geometry buffers currently stored on the GPU.",
        format: (m) => (m.current ? formatNumber(m.current.geometries) : null),
    },
    {
        icon: LuImage,
        label: "Textures",
        description: "Number of textures currently stored on the GPU.",
        format: (m) => (m.current ? formatNumber(m.current.textures) : null),
    },
    {
        icon: LuLoader,
        label: "Model load time",
        description: "Time from starting resource loading to the model being ready.",
        format: (m) => (m.modelLoadTime !== null ? `${m.modelLoadTime.toFixed(0)} ms` : null),
        variant: (m) => {
            if (m.modelLoadTime === null) return "info";
            if (m.modelLoadTime <= 2_000) return "success";
            if (m.modelLoadTime <= 5_000) return "warning";

            return "error";
        },
    },
];

export default function PerformanceList() {
    const perf = useSyncExternalStore(DiagnosticsStore.subscribe, () => DiagnosticsStore.performance);

    if (!perf.current && perf.modelLoadTime === null) {
        return <DiagnosticsEmpty>No render data collected yet!</DiagnosticsEmpty>;
    }

    return (
        <DiagnosticsGrid size="sm">
            {METRICS.map((metric) => {
                const value = metric.format(perf);
                const variant =
                    typeof metric.variant === "function" ? metric.variant(perf) : (metric.variant ?? "info");

                return (
                    <DiagnosticsCard key={metric.label} variant={variant}>
                        <DiagnosticsCardHeader>
                            <DiagnosticsCardIcon icon={metric.icon} />
                            <DiagnosticsCardLabel>{metric.label}</DiagnosticsCardLabel>
                            <DiagnosticsCardDescription>{metric.description}</DiagnosticsCardDescription>
                        </DiagnosticsCardHeader>
                        <DiagnosticsCardValue>
                            {value ?? <DiagnosticsCardNull>—</DiagnosticsCardNull>}
                        </DiagnosticsCardValue>
                    </DiagnosticsCard>
                );
            })}
        </DiagnosticsGrid>
    );
}
